/* Video timestamps (YouTube + Vimeo) - v2 (independent from v1), queued seeks, duration auto, active chapter, height sync */
(function () {
  function loadYouTubeAPI(cb) {
    if (window.YT && window.YT.Player) return cb();

    window.__aiYtReadyCbs = window.__aiYtReadyCbs || [];
    window.__aiYtReadyCbs.push(cb);

    if (window.__aiYtScriptLoading) return;
    window.__aiYtScriptLoading = true;

    var prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof prev === "function") prev();
      var cbs = window.__aiYtReadyCbs || [];
      window.__aiYtReadyCbs = [];
      cbs.forEach(function (fn) { try { fn(); } catch (e) {} });
    };

    var tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    var firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }

  function loadVimeoAPI(cb) {
    if (window.Vimeo && window.Vimeo.Player) return cb();

    window.__aiVimeoReadyCbs = window.__aiVimeoReadyCbs || [];
    window.__aiVimeoReadyCbs.push(cb);

    if (window.__aiVimeoScriptLoading) return;
    window.__aiVimeoScriptLoading = true;

    var script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.onload = function () {
      var cbs = window.__aiVimeoReadyCbs || [];
      window.__aiVimeoReadyCbs = [];
      cbs.forEach(function (fn) { try { fn(); } catch (e) {} });
    };
    document.head.appendChild(script);
  }

  class VideoTimestampsElementV2 extends HTMLElement {
    constructor() {
      super();
      this.videoFrame = null;
      this.videoProvider = null;
      this.player = null;
      this.isReady = false;
      this.pendingSeek = null;

      this.timestampItems = [];
      this.durationEl = null;
      this.durationManual = false;

      this._timer = null;
      this._ro = null;
      this._mo = null;
      this._resizeHandler = null;
      this._rAfTicks = 0;
      this._rAfId = null;
    }

    connectedCallback() {
      this.videoFrame = this.querySelector("[data-video-provider]");
      this.durationEl = this.querySelector("[data-duration-output]");
      this.durationManual = this.durationEl
        ? this.durationEl.getAttribute("data-duration-manual") === "1"
        : false;

      this.refreshTimestampItems(true);

      if (this.videoFrame) {
        this.videoProvider = this.videoFrame.getAttribute("data-video-provider");
        this.initializePlayer();
      }

      this.startHeightSync();
      this.observeInternalMutations();
    }

    disconnectedCallback() {
      if (this._timer) clearInterval(this._timer);
      if (this._ro) this._ro.disconnect();
      if (this._mo) this._mo.disconnect();
      if (this._resizeHandler) window.removeEventListener("resize", this._resizeHandler);
      if (this._rAfId) cancelAnimationFrame(this._rAfId);
    }

    // -------------------- Timestamp binding --------------------
    refreshTimestampItems(bind) {
      this.timestampItems = Array.prototype.slice.call(
        this.querySelectorAll("[data-timestamp]")
      );

      if (!bind) return;

      this.timestampItems.forEach((item) => {
        // remove old handlers (theme editor re-renders)
        if (item.__aiTsV2Click) item.removeEventListener("click", item.__aiTsV2Click);
        if (item.__aiTsV2Key) item.removeEventListener("keydown", item.__aiTsV2Key);

        item.__aiTsV2Click = () => this.handleTimestampClick(item);
        item.__aiTsV2Key = (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.handleTimestampClick(item);
          }
        };

        item.addEventListener("click", item.__aiTsV2Click);
        item.addEventListener("keydown", item.__aiTsV2Key);
      });
    }

    // -------------------- Player init --------------------
    initializePlayer() {
      if (this.videoProvider === "youtube") {
        loadYouTubeAPI(() => this.createYouTubePlayer());
      } else if (this.videoProvider === "vimeo") {
        loadVimeoAPI(() => this.createVimeoPlayer());
      }
    }

    ensureIframeId() {
      if (!this.videoFrame) return null;
      if (!this.videoFrame.id) {
        this.videoFrame.id = "ai-video-v2-" + Math.random().toString(36).slice(2);
      }
      return this.videoFrame.id;
    }

    createYouTubePlayer() {
      if (!window.YT || !window.YT.Player || !this.videoFrame) return;

      var iframeId = this.ensureIframeId();
      if (!iframeId) return;

      try {
        this.player = new YT.Player(iframeId, {
          events: {
            onReady: () => {
              this.isReady = true;
              this.trySetDurationFromPlayer();

              if (this.pendingSeek != null) {
                try {
                  this.player.seekTo(this.pendingSeek, true);
                  this.player.playVideo();
                } catch (e) {}
                this.pendingSeek = null;
              }
              this.setupTimeTracking();
            }
          }
        });
      } catch (e) {}
    }

    createVimeoPlayer() {
      if (!window.Vimeo || !window.Vimeo.Player || !this.videoFrame) return;

      try {
        this.player = new Vimeo.Player(this.videoFrame);
      } catch (e) {
        return;
      }

      this.player.ready().then(() => {
        this.isReady = true;
        this.trySetDurationFromPlayer();

        if (this.pendingSeek != null) {
          this.player.setCurrentTime(this.pendingSeek);
          this.player.play();
          this.pendingSeek = null;
        }
        this.setupTimeTracking();
      }).catch(() => {});
    }

    // -------------------- Seeking + active chapter --------------------
    handleTimestampClick(item) {
      const seconds = this.parseTimestamp(item.getAttribute("data-timestamp"));
      if (isNaN(seconds)) return;

      // Always set active immediately
      this.timestampItems.forEach((i) => i.classList.remove("active"));
      item.classList.add("active");

      if (!this.player || !this.isReady) {
        this.pendingSeek = seconds;
        return;
      }

      if (this.videoProvider === "youtube" && this.player.seekTo) {
        try {
          this.player.seekTo(seconds, true);
          this.player.playVideo();
        } catch (e) {}
      } else if (this.videoProvider === "vimeo") {
        this.player.setCurrentTime(seconds).then(() => {
          this.player.play();
        }).catch(() => {});
      }
    }

    setupTimeTracking() {
      if (this._timer) clearInterval(this._timer);

      if (this.videoProvider === "youtube" && this.player) {
        this._timer = setInterval(() => {
          try {
            if (this.player.getPlayerState && this.player.getPlayerState() === 1) {
              const t = this.player.getCurrentTime();
              if (typeof t === "number") this.updateActiveTimestamp(t);
            }
          } catch (e) {}
        }, 400);
      } else if (this.videoProvider === "vimeo" && this.player) {
        if (this.player.off) this.player.off("timeupdate");
        this.player.on("timeupdate", (data) => {
          this.updateActiveTimestamp(data.seconds);
        });
      }
    }

    updateActiveTimestamp(currentTime) {
      if (!this.timestampItems || !this.timestampItems.length) {
        this.refreshTimestampItems(false);
      }

      let activeItem = null;
      this.timestampItems.forEach((item) => {
        const seconds = this.parseTimestamp(item.getAttribute("data-timestamp"));
        if (!isNaN(seconds) && currentTime >= seconds) activeItem = item;
      });

      this.timestampItems.forEach((item) => item.classList.remove("active"));
      if (activeItem) activeItem.classList.add("active");
    }

    parseTimestamp(ts) {
      const parts = String(ts || "").trim().split(":").reverse();
      let seconds = 0;
      parts.forEach((p, i) => {
        const n = parseInt(p, 10);
        if (!isNaN(n)) seconds += n * Math.pow(60, i);
      });
      return seconds;
    }

    // -------------------- Duration auto --------------------
    trySetDurationFromPlayer() {
      if (!this.durationEl) return;
      if (this.durationManual) return;

      const prefix = (this.getAttribute("data-duration-prefix") || "Duration").trim();
      const prefixText = prefix ? prefix + ": " : "";

      if (this.videoProvider === "youtube" && this.player && this.player.getDuration) {
        let tries = 0;
        const tick = () => {
          tries++;
          let d = 0;
          try { d = this.player.getDuration(); } catch (e) {}
          if (typeof d === "number" && d > 0) {
            this.durationEl.textContent = prefixText + this.formatDuration(d);
          } else if (tries < 40) {
            setTimeout(tick, 250);
          }
        };
        tick();
      }

      if (this.videoProvider === "vimeo" && this.player && this.player.getDuration) {
        this.player.getDuration().then((d) => {
          if (typeof d === "number" && d > 0) {
            this.durationEl.textContent = prefixText + this.formatDuration(d);
          }
        }).catch(() => {});
      }
    }

    formatDuration(totalSeconds) {
      const s = Math.max(0, Math.round(totalSeconds));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const r = s % 60;
      if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
      return `${m}:${String(r).padStart(2, "0")}`;
    }

    // -------------------- Height sync (list shouldn’t exceed video frame) --------------------
    startHeightSync() {
      const wrapper = this.querySelector("[data-video-wrapper]");
      if (!wrapper) return;

      const apply = () => {
        const rect = wrapper.getBoundingClientRect();
        if (rect && rect.height) {
          this.style.setProperty("--ai-video-height", rect.height + "px");
        }
      };

      apply();

      // ResizeObserver (best)
      if (window.ResizeObserver) {
        if (this._ro) this._ro.disconnect();
        this._ro = new ResizeObserver(apply);
        this._ro.observe(wrapper);
      }

      // A few RAF ticks for initial layout/fonts
      this._rAfTicks = 0;
      const rafTick = () => {
        this._rAfTicks++;
        apply();
        if (this._rAfTicks < 120) {
          this._rAfId = requestAnimationFrame(rafTick);
        }
      };
      this._rAfId = requestAnimationFrame(rafTick);

      // Window resize fallback
      this._resizeHandler = () => apply();
      window.addEventListener("resize", this._resizeHandler, { passive: true });
    }

    // Theme editor can re-render blocks/iframe; keep things wired up
    observeInternalMutations() {
      if (this._mo) this._mo.disconnect();

      this._mo = new MutationObserver(() => {
        const newFrame = this.querySelector("[data-video-provider]");
        if (newFrame && newFrame !== this.videoFrame) {
          this.videoFrame = newFrame;
          this.videoProvider = this.videoFrame.getAttribute("data-video-provider");
          this.player = null;
          this.isReady = false;
          this.pendingSeek = null;
          this.initializePlayer();
        }
        this.refreshTimestampItems(true);
        this.startHeightSync();
      });

      this._mo.observe(this, { childList: true, subtree: true });
    }
  }

  // v2-only guard (does NOT conflict with v1)
  if (!window.__videoTimestampsBaseDefinedV2) {
    window.__videoTimestampsBaseDefinedV2 = true;

    function defineForTag(el) {
      if (!el || !el.tagName) return;
      var name = el.tagName.toLowerCase();
      if (name.startsWith("video-timestamps-") && !customElements.get(name)) {
        try { customElements.define(name, VideoTimestampsElementV2); } catch (e) {}
      }
    }

    // Existing
    document.querySelectorAll("*").forEach(defineForTag);

    // Future
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes && m.addedNodes.forEach(function (node) {
          if (!node || node.nodeType !== 1) return;
          defineForTag(node);
          if (node.querySelectorAll) node.querySelectorAll("*").forEach(defineForTag);
        });
      });
    });

    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
